import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  styled,
  Divider,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getJobBoardsByName, getJobStatsPostedOnJobBoards, IJobBoard } from 'wisestep-api';
import { WsColors, WsTypoColors, WsTypography, WsTypoVariants } from 'wisestep-base';

import { useUserContext } from '../../../../../context';
import {
  IPremiumJobBoard,
  jobBoardsListUS,
} from '../../../../../features/jobs/utils/JobListSideNavHelper';
import { JobSearchFilter, JobStatusCount } from '../../JObSearchFilterDefaultQuery';

import SideNavJobBoardItem from './SideNavJobBoardItem';

interface TypoProps {
  textColor: string;
}
const TypographyTheme = styled(Typography)(({ textColor }: TypoProps) => ({
  color: textColor,
  fontSize: '18px',
  textAlign: 'right',
}));
const BoxNavTheme = styled(Box)(() => ({
  borderRadius: 4,
  maxWidth: 273,
  backgroundColor: WsColors.White,
}));
interface JobListSideNavProps {
  selectedIndexValue: (index: number, value: string) => void;
  jobStats: JobStatusCount;
  query: JobSearchFilter;
  onJobBoardClick: (jobBoardName: string) => void;
}

const JobListSideNav = ({
  selectedIndexValue,
  jobStats,
  onJobBoardClick,
  query,
}: JobListSideNavProps) => {
  const statusArray = ['All', 'Open', 'Closed'];
  const mapStatus = new Map();
  mapStatus.set('All', jobStats.all);
  mapStatus.set('Open', jobStats.open);
  mapStatus.set('Closed', jobStats.closed);
  const [selectedIndex, setSelectedIndex] = useState<number>(
    statusArray.indexOf(query.jobStatus ?? 'All')
  );
  const onChange = (index: number, value: string) => {
    selectedIndexValue(index, value);
    setSelectedIndex(index);
  };
  const loggedInUser = useUserContext().userDTO?.email ?? '';

  const [jobBoardPostingData, setJobBoardPostingData] =
    useState<IPremiumJobBoard[]>(jobBoardsListUS);
  const jobBoardArrayTempData = useRef<IPremiumJobBoard[]>(jobBoardsListUS);

  const sharedUserCount = useMemo(() => {
    return (jobBoard: IJobBoard) => {
      let count = 0;
      jobBoard.jobBoardSharedUsers?.forEach((jobBoardSharedUser) => {
        if (jobBoardSharedUser.email == loggedInUser)
          count = count + jobBoardSharedUser.maxPostingsPerMonth;
      });
      return count;
    };
  }, [loggedInUser]);

  useEffect(() => {
    const setAllJobBoard = async () => {
      const [response, response2] = await Promise.all([
        getJobBoardsByName('Monster,Dice,TechFetch,CareerBuilder,CWJobs,MonsterUK'),
        getJobStatsPostedOnJobBoards(),
      ]);
      response.data.map((jobBoardResponse) => {
        const availableCredits = sharedUserCount(jobBoardResponse);
        if (availableCredits > 0) {
          const updatedJobBoardPostingData: IPremiumJobBoard[] = [
            ...jobBoardArrayTempData.current,
          ].filter((item) => item.name != jobBoardResponse.jobBoardName);
          const jobBoard = jobBoardArrayTempData.current.find(
            (item) => item.name == jobBoardResponse.jobBoardName
          );
          if (jobBoard) {
            jobBoard.availableCredits = availableCredits;
            jobBoard.usedCredits = response2.data[`${jobBoardResponse.jobBoardName}Count`] ?? 0;
            jobBoard.showJobBoard = true;
            jobBoardArrayTempData.current = [...updatedJobBoardPostingData, jobBoard];
          }
        }
      });
      setJobBoardPostingData(jobBoardArrayTempData.current);
    };
    setAllJobBoard();
  }, [loggedInUser, sharedUserCount]);

  return (
    <BoxNavTheme>
      <List sx={{ width: '100%', pb: 2, cursor: 'pointer' }} component="nav">
        <Box p={2}>
          <WsTypography
            variant={WsTypoVariants.SUB_TITLE}
            color={WsTypoColors.SECONDARY}
            cursor="pointer"
          >
            Job Status
          </WsTypography>
        </Box>
        {statusArray.map((status, index) => (
          <ListItemButton
            id={`job_status_${index}`}
            sx={{
              background: index === selectedIndex ? WsColors.TheBlues100 : WsColors.White,
              cursor: 'pointer',
            }}
            key={index}
            onClick={() => onChange(index, status)}
          >
            <ListItemText
              primary={
                <WsTypography
                  variant={WsTypoVariants.BODY1}
                  color={WsTypoColors.BLACK}
                  cursor="pointer"
                >
                  {status}
                </WsTypography>
              }
            />
            <TypographyTheme textColor={WsColors.GenericRichBlack}>
              {mapStatus.get(status)}
            </TypographyTheme>
          </ListItemButton>
        ))}

        {jobBoardPostingData
          ?.filter((jobBoard) => jobBoard.jobBoardType == 'US')
          .find((jobBoard) => jobBoard.showJobBoard) && (
          <>
            <Box py={1}>
              <Divider />
            </Box>
            <Box>
              <Box bgcolor={WsColors.TheBlues50} p={2}>
                <WsTypography
                  variant={WsTypoVariants.SUB_TITLE}
                  color={WsTypoColors.PRIMARY}
                  cursor="pointer"
                >
                  US Job Boards
                </WsTypography>
              </Box>
              <Box py={1}>
                <Divider />
              </Box>
              {jobBoardPostingData?.map(
                (jobBoard, index) =>
                  jobBoard.showJobBoard && (
                    <SideNavJobBoardItem
                      key={index}
                      onJobBoardClick={onJobBoardClick}
                      jobBoard={jobBoard}
                    />
                  )
              )}
            </Box>
          </>
        )}
        {jobBoardPostingData
          ?.filter((jobBoard) => jobBoard.jobBoardType == 'UK')
          .find((jobBoard) => jobBoard.showJobBoard) && (
          <>
            <Box py={1}>
              <Divider />
            </Box>
            <Box>
              <Box bgcolor={WsColors.TheBlues50} p={2}>
                <WsTypography
                  variant={WsTypoVariants.SUB_TITLE}
                  color={WsTypoColors.PRIMARY}
                  cursor="pointer"
                >
                  UK Job Boards
                </WsTypography>
              </Box>
              <Box py={1}>
                <Divider />
              </Box>
              {jobBoardPostingData?.map(
                (jobBoard, index) =>
                  jobBoard.showJobBoard && (
                    <SideNavJobBoardItem
                      key={index}
                      onJobBoardClick={onJobBoardClick}
                      jobBoard={jobBoard}
                    />
                  )
              )}
            </Box>
          </>
        )}
      </List>
    </BoxNavTheme>
  );
};
export default JobListSideNav;
